import {ReactNode} from 'react';

import {ProductGroup} from 'core/constants/productGroups';
import {
    AuthenticDetectionContext,
    AuthenticStatus,
    ProductOperation,
    ProductStatus,
} from 'core/dictionaries';

import {ApiFilters, ApiPaging, ApiSortings} from './api';
import {AggregationStorageLocation} from './documents';
import {PackageTypes} from './gtins';
import {
    BlockingDocumentStatus,
    BlockingScopes,
    BlockingSourceFormation,
    BlockingType,
    BlokcingReason,
} from './products-blocking';

export interface CodeBlockingData {
    blockingId: string;
    reason: BlokcingReason;
    scopes: BlockingScopes[];
    createdAt: string;
    sourceFormation: BlockingSourceFormation;
    organization: string;
    documentId: string;
    sourceReportId: string;
    sourceReportStatus: BlockingDocumentStatus;
    removable: boolean;
    sourceReportType: BlockingType;
}

export interface ProductCommonRaw {
    parentMarkingCode: string | null;
    markingCode: string;
    packagingLevel: number;
    fileLink: string;
    status: ProductStatus | null;
    product: {
        gtin: string;
        productTrademark: string;
        manufacturerName: string;
        productName: string;
        batchNumber: string;
        manufacturingDate: string;
        productGroupCode: ProductGroup;
    };
    statuses: {
        [key: string]: {
            status: ProductStatus;
            operationTime: string;
            parentMarkingCode: string;
        };
    };
    packagingType: PackageTypes;
    childrenTotalCount: number;
    owner: string | null;
    storageLocation: AggregationStorageLocation | null;
    markingCodeAuthenticInfo: Partial<{
        authenticStatus: AuthenticStatus;
        authenticDetectionContext: AuthenticDetectionContext;
        authenticDetectionDateTime: string;
        authenticReason: string;
        documentId: string;
    }> | null;
    replacedByMarkingCodeId?: string | null;
    replacesMarkingCodeId?: string | null;
    blockings: CodeBlockingData[];
}

export interface ProductRaw extends ProductCommonRaw {
    operations: ProductOperationsRawRow[];
}

export interface ProductFormatted {
    name: string;
    code: string;
    status: string;
    brand: string;
    gtin: string;
    generatedTime: string;
    issuedTime: string;
    appliedTime: string;
    aggregationInfo: ReactNode;
    enteredCirculationTime: string;
    leftCirculationTime: string;
    fileLink: string;
    packagingLevel: number;
    isAggregate: boolean;
    isGroup: boolean;
    storageLocation: string;
    storageName: string;
    packagingType?: string;
    productGroupCode?: ReactNode;
    authentic?: {
        status: ReactNode;
        date: string;
        type: string;
        link: ReactNode | string;
    };
    batchNumber?: string;
    isTransportPackage?: boolean;
    manufacturingDate?: string;
}

export interface ProductOperationsRawRow {
    operationAction: ProductOperation;
    operationTime: string;
    orderId: string | null;
    reportId: string | null;
    clientName: string;
    source: 'CUSTOMS' | string | null;
    fromStorageLocationId: string | null;
    toStorageLocationId: string | null;
    fromStorageLocationName: string | null;
    toStorageLocationName: string | null;
    reason: string | null;
}

export enum ProductOperationsAccessors {
    documentType = 'operationAction',
    date = 'operationTime',
    orderId = 'orderId',
    documentId = 'reportId',
    clientName = 'clientName',
    fromStorageLocationName = 'fromStorageLocationName',
    toStorageLocationName = 'toStorageLocationName',
    reason = 'reason',
}

export interface ProductTracing {
    costVatFree: string;
    costWithVat: string;
    gtin: string;
    id: string;
    inn: string;
    markingCode: string;
    name: string;
    price: string;
    quantity: string;
    updDate: string;
}

export interface ProductOperationsFormattedRow {
    [ProductOperationsAccessors.documentType]: string;
    [ProductOperationsAccessors.orderId]?: string;
    [ProductOperationsAccessors.date]: string;
    [ProductOperationsAccessors.clientName]: string;
    [ProductOperationsAccessors.documentId]: ReactNode;
    [ProductOperationsAccessors.fromStorageLocationName]: string;
    [ProductOperationsAccessors.toStorageLocationName]: string;
    [ProductOperationsAccessors.reason]: string;
}

export interface ProductOperationsSelectOption<T extends string | number = string> {
    label: string;
    value: T;
}

export interface ProductOperationsReasonOption {
    value: string;
    operationAction: ProductOperation;
}

export interface ProductOperationsDictionary {
    reason: ProductOperationsReasonOption[];
    fromStorageLocation: ProductOperationsSelectOption<number>[];
    toStorageLocation: ProductOperationsSelectOption<number>[];
}

export interface ProductOperationFilter {
    operations?: ProductOperation[];
    changeDateFrom?: string;
    changeDateTo?: string;
    documentNumber?: string;
    documentSender?: string;
    fromStorageLocationId?: number;
    toStorageLocationId?: number;
    reason?: string;
}

export type RemarkingReason = 'DAMAGED' | 'LOST' | 'MISPRINT' | 'OTHER';

export interface ProductRemarkingHistoryRow {
    oldCode: string;
    newCode: string;
    operationTime?: string;
    reportId?: string | null;
    reason?: RemarkingReason;
    initiator?: string | null;
}

export interface ProductRemarkingHistory {
    chain: string[];
    rows: ProductRemarkingHistoryRow[];
}

export interface ProductContentRawRow {
    gtin: string;
    productName: string;
    markingCode: string;
    status: ProductStatus | null;
    modifiedAt: string;
    packagingType: PackageTypes;
    manufacturerName: string;
    productGroupCode: ProductGroup;
    authenticStatus: AuthenticStatus;
    parentMarkingCode: string | null;
    parentGtin: string | null;
    owner: string | null;
    storageLocationAddress: string | null;
    disaggregationMethod: 'MANUAL' | 'AUTOMATIC';
}

export interface ProductContentRawResponse {
    nextPageRef: string;
    data: ProductContentRawRow[];
}

export interface ProductContentRawRequest {
    reportId: string;
    paging: ApiPaging | {pageRef: string | null};
    sortings: ApiSortings;
    filters: ApiFilters;
    pageRef?: string | null;
}

export enum ProductContentAccessors {
    name = 'productName',
    code = 'markingCode',
    status = 'status',
    modifiedDate = 'modifiedAt',
    packageType = 'packagingType',
    manufacturer = 'manufacturerName',
    productGroupCode = 'productGroupCode',
    owner = 'owner',
    storageLocationAddress = 'storageLocationAddress',
    authenticStatus = 'authenticStatus',
    parentMarkingCode = 'parentMarkingCode',
    disaggregationMethod = 'disaggregationMethod',
}

export interface ProductContentFormattedRow {
    [ProductContentAccessors.name]: string;
    [ProductContentAccessors.code]: ReactNode;
    [ProductContentAccessors.status]: string;
    [ProductContentAccessors.modifiedDate]: string;
    [ProductContentAccessors.packageType]: string;
    [ProductContentAccessors.manufacturer]: string;
    [ProductContentAccessors.owner]?: string;
    [ProductContentAccessors.storageLocationAddress]?: string;
    [ProductContentAccessors.authenticStatus]?: string;
    [ProductContentAccessors.parentMarkingCode]?: ReactNode | string;
    [ProductContentAccessors.disaggregationMethod]?: string;
}
